/**
 * Basemap tiers for the homepage map, tried in order. A tier is used only when it is configured,
 * and the next tier takes over when the current one stops delivering tiles. The map's markers and
 * search do not depend on any tier, so they keep working through every switch.
 *
 * CARTO is used only with a key. Without one, its anonymous tiles are a watermark, so it is never
 * requested. Mapbox needs the public access token the build already has. OpenStreetMap needs no
 * key, but its tile policy asks heavy sites to run their own tile server, so it is a last resort
 * for brief outages, not a steady-state basemap. The Mapbox style id should be confirmed in the
 * Mapbox account before relying on it.
 */

export const BASEMAP = {
  CARTO: 'carto',
  MAPBOX: 'mapbox',
  OSM: 'osm',
};

const ATTRIBUTION = {
  [BASEMAP.CARTO]:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
  [BASEMAP.MAPBOX]:
    '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  [BASEMAP.OSM]:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
};

/**
 * @param {{ cartoKey?: string, mapboxToken?: string }} config build-time values
 * @returns {Array<{ id: string, url: string, options: object }>} configured tiers, in order
 */
export const basemapTiers = ({ cartoKey = '', mapboxToken = '' } = {}) => {
  const tiers = [];
  if (cartoKey) {
    tiers.push({
      id: BASEMAP.CARTO,
      url: `https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${cartoKey}`,
      options: { attribution: ATTRIBUTION[BASEMAP.CARTO], maxZoom: 19, subdomains: 'abcd' },
    });
  }
  if (mapboxToken) {
    tiers.push({
      id: BASEMAP.MAPBOX,
      url: `https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/256/{z}/{x}/{y}@2x?access_token=${mapboxToken}`,
      options: {
        attribution: ATTRIBUTION[BASEMAP.MAPBOX],
        maxZoom: 19,
        tileSize: 256,
        zoomOffset: 0,
      },
    });
  }
  tiers.push({
    id: BASEMAP.OSM,
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    options: { attribution: ATTRIBUTION[BASEMAP.OSM], maxZoom: 19 },
  });
  return tiers;
};

/**
 * Shows the first tier, and moves to the next when it fails. A tier has failed when no tile has
 * loaded within `loadTimeoutMs`, or when `errorThreshold` tiles have errored with none loaded.
 * Once a tier has loaded any tile it is kept, and a later error does not switch it.
 *
 * @param {object} L Leaflet namespace
 * @param {object} map Leaflet map
 * @param {Array} tiers from basemapTiers()
 * @param {{ loadTimeoutMs?: number, errorThreshold?: number, onTierChange?: Function, onAllFailed?: Function }} options
 * @returns {Function} stop: removes the current tier and clears the timer
 */
export const attachBasemapWithFailover = (
  L,
  map,
  tiers,
  { loadTimeoutMs = 10000, errorThreshold = 8, onTierChange, onAllFailed } = {}
) => {
  let index = 0;
  let layer = null;
  let loaded = 0;
  let errors = 0;
  let timer = null;
  let stopped = false;

  const clear = () => {
    clearTimeout(timer);
    timer = null;
  };

  const fail = () => {
    if (stopped) return;
    clear();
    if (layer) layer.remove();
    layer = null;
    index += 1;
    if (index >= tiers.length) {
      if (onAllFailed) onAllFailed();
      return;
    }
    start();
  };

  function start() {
    const tier = tiers[index];
    loaded = 0;
    errors = 0;
    layer = L.tileLayer(tier.url, tier.options);
    layer.on('tileload', () => {
      loaded += 1;
      clear();
    });
    layer.on('tileerror', () => {
      errors += 1;
      if (loaded === 0 && errors >= errorThreshold) fail();
    });
    layer.addTo(map);
    timer = setTimeout(() => {
      if (loaded === 0) fail();
    }, loadTimeoutMs);
    if (onTierChange) onTierChange(tier.id, index);
  }

  if (tiers.length === 0) {
    if (onAllFailed) onAllFailed();
    return () => {};
  }
  start();

  return () => {
    stopped = true;
    clear();
    if (layer) layer.remove();
    layer = null;
  };
};
